import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  getShortlistReview,
  previewShortlistAccount,
  previewShortlistResult,
  sendShortlistAccountSetup,
  sendShortlistResults,
  type ShortlistEmailPreview,
  type ShortlistReviewArchitect,
  type ShortlistSendResult,
} from '../lib/api';
import { LoadingScreen } from '../components/LoadingScreen';
import { WAF_YELLOW_DARK } from '../theme';
import { useAuth } from '../contexts/AuthContext';

// Primary (WAF-yellow) button style used across the app for headline actions.
const primaryBtnSx = {
  bgcolor: WAF_YELLOW_DARK,
  color: '#111827',
  '&:hover': { bgcolor: '#E6B800' },
} as const;

interface PreviewState {
  title: string;
  preview: ShortlistEmailPreview;
}

export function OnboardingReview() {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  // Only super-admins (Admin) may actually send; EMAP (User) can view + preview.
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');

  const [reviewLoading, setReviewLoading] = useState(true);
  const [architects, setArchitects] = useState<ShortlistReviewArchitect[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Confirm dialog: 'results' | 'account' | null
  const [confirm, setConfirm] = useState<'results' | 'account' | null>(null);

  // Email preview dialog state.
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadReview = useCallback(async () => {
    setReviewLoading(true);
    setError(null);
    try {
      const data = await getShortlistReview();
      setArchitects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load review');
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReview();
  }, [loadReview]);

  const totals = useMemo(() => {
    const entry_count = architects.reduce((n, a) => n + a.entry_count, 0);
    const entries_sent = architects.reduce((n, a) => n + a.entries_sent, 0);
    const accountsSent = architects.filter((a) => a.account_sent_at).length;
    return { entry_count, entries_sent, accountsSent };
  }, [architects]);

  const toggleExpanded = (user_id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(user_id)) next.delete(user_id);
      else next.add(user_id);
      return next;
    });
  };

  const openResultPreview = async (assignment_id: number, label: string) => {
    setPreview(null);
    setPreviewLoading(true);
    setError(null);
    try {
      const p = await previewShortlistResult(assignment_id);
      setPreview({ title: label, preview: p });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openAccountPreview = async (user_id: string, label: string) => {
    setPreview(null);
    setPreviewLoading(true);
    setError(null);
    try {
      const p = await previewShortlistAccount(user_id);
      setPreview({ title: label, preview: p });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const summariseSend = (res: ShortlistSendResult) =>
    `Sent ${res.sent}, failed ${res.failed}, skipped ${res.skipped}.`;

  const onConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    setError(null);
    try {
      if (confirm === 'results') {
        const res = await sendShortlistResults();
        setSnack(`Result emails — ${summariseSend(res)}`);
        await loadReview();
      } else if (confirm === 'account') {
        const res = await sendShortlistAccountSetup();
        setSnack(`Account-setup emails — ${summariseSend(res)}`);
        await loadReview();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const confirmCopy: Record<'results' | 'account', { title: string; body: string; cta: string }> = {
    results: {
      title: 'Send result emails?',
      body: `This sends one result email per staged entry (${totals.entry_count} across ${architects.length} architect(s)).`,
      cta: 'Send result emails',
    },
    account: {
      title: 'Send account-setup emails?',
      body: `This sends one account-setup email per architect (${architects.length}). If email approval is enabled, emails will be staged in the email log section`,
      cta: 'Send account-setup emails',
    },
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h3" component="h1" align="center">
        Review
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ---- Send bar ---- */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          position: { md: 'sticky' },
          top: { md: 16 },
          zIndex: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          useFlexGap
          sx={{ flexWrap: 'wrap' }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {totals.entry_count} entr{totals.entry_count === 1 ? 'y' : 'ies'} ·{' '}
            {totals.entries_sent} result(s) sent · {totals.accountsSent}/
            {architects.length} accounts set up
          </Typography>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={busy || architects.length === 0 || !isAdmin}
            onClick={() => setConfirm('results')}
            sx={{ ...primaryBtnSx, width: { xs: '100%', sm: 'auto' } }}
          >
            Send result emails
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddAltIcon />}
            disabled={busy || architects.length === 0 || !isAdmin}
            onClick={() => setConfirm('account')}
            sx={{ ...primaryBtnSx, width: { xs: '100%', sm: 'auto' } }}
          >
            Send account-setup emails
          </Button>
        </Stack>
        {!isAdmin && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Only super admins can send result and account-setup emails.
          </Typography>
        )}
      </Paper>

      {/* ---- Review tree ---- */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Review</Typography>
        </Box>

        {reviewLoading ? (
          <LoadingScreen label="Loading review…" minHeight={280} />
        ) : architects.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Nothing staged yet. Import a judging workbook to begin.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {architects.map((a) => {
              const open = expanded.has(a.user_id);
              return (
                <Box key={a.user_id}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                        onClick={() =>
                          void openAccountPreview(
                            a.user_id,
                            `Account setup — ${a.display_name}`
                          )
                        }
                        sx={{ ...primaryBtnSx, mr: { xs: 0, sm: 1 } }}
                      >
                        Preview account email
                      </Button>
                    }
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                  >
                    <ListItemButton onClick={() => toggleExpanded(a.user_id)}>
                      <IconButton edge="start" size="small" sx={{ mr: 1 }}>
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            useFlexGap
                            sx={{ flexWrap: 'wrap', pr: { xs: 0, sm: 20 } }}
                          >
                            <Typography component="span" fontWeight={600}>
                              {a.display_name}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${a.entry_count} entr${a.entry_count === 1 ? 'y' : 'ies'}`}
                            />
                            <Chip
                              size="small"
                              color={a.account_sent_at ? 'success' : 'default'}
                              label={a.account_sent_at ? 'Account sent' : a.account_status || 'Pending'}
                            />
                          </Stack>
                        }
                        secondary={a.email}
                      />
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={open} unmountOnExit>
                    <List dense disablePadding sx={{ bgcolor: 'action.hover' }}>
                      {a.entries.map((e) => (
                        <ListItem
                          key={e.assignment_id}
                          secondaryAction={
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                              onClick={() =>
                                void openResultPreview(
                                  e.assignment_id,
                                  `${e.project_title} (${e.nomination_code})`
                                )
                              }
                              sx={primaryBtnSx}
                            >
                              Preview
                            </Button>
                          }
                          sx={{ pl: 7, borderBottom: 1, borderColor: 'divider' }}
                        >
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                useFlexGap
                                sx={{ flexWrap: 'wrap', pr: { xs: 0, sm: 12 } }}
                              >
                                <Typography component="span" variant="body2" fontWeight={600}>
                                  {e.project_title || '(untitled)'}
                                </Typography>
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {e.nomination_code}
                                </Typography>
                                <Chip
                                  size="small"
                                  color={e.status === 'sent' ? 'success' : e.status === 'failed' ? 'error' : 'default'}
                                  label={e.status}
                                />
                              </Stack>
                            }
                            secondary={
                              <>
                                Assigned email template: {e.template_name}
                                {e.status_detail ? ` — ${e.status_detail}` : ''}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </Paper>

      {/* ---- Confirm dialog ---- */}
      <Dialog open={confirm !== null} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm && (
          <>
            <DialogTitle>{confirmCopy[confirm].title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{confirmCopy[confirm].body}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => void onConfirm()}
                disabled={busy}
                sx={primaryBtnSx}
              >
                {confirmCopy[confirm].cta}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ---- Email preview dialog ---- */}
      <Dialog
        open={preview !== null || previewLoading}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{preview?.title ?? 'Loading preview…'}</DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <LoadingScreen label="Rendering email…" minHeight={200} />
          ) : preview ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Subject
              </Typography>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {preview.preview.subject || '(no subject)'}
              </Typography>
              <Divider sx={{ my: 1 }} />
              {/* Sandboxed render: srcDoc isolates the email's own styles/scripts. */}
              <Box
                component="iframe"
                title="Email preview"
                srcDoc={preview.preview.html}
                sandbox=""
                sx={{ width: '100%', height: 480, border: 0 }}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}
